/**
 * Reference-language lookups (design D29; av-methodology-domain spec).
 *
 * `reference-languages.json` is the interpretation layer's own catalog —
 * matrix COLUMN (structural, 1-indexed) → reference language/medium →
 * communicational profile. This module is the only place that catalog is
 * read from. Design D38 (author reversal of D29/D36/D37) makes one
 * exception to D29's original "matrix.ts MUST NOT import it" rule:
 * `matrix.ts`'s column-header render loop now calls
 * `referenceLanguageForArchetype()` to render the label as the header's
 * visible text. Every other restriction from D29 still holds — no other
 * matrix-side module reads this catalog, and cell/node accessible labels
 * (aria-label) stay numeric ("posição N"), never a reference-language name.
 */

import { referenceLanguages } from "./data-source";
import type { ArchetypeId, ReferenceLanguageEntry } from "./types";

/** Looks up a column's (1-indexed) reference-language entry. */
export function referenceLanguageForColumn(column: number): ReferenceLanguageEntry | undefined {
  return referenceLanguages.find((entry) => entry.column === column);
}

/** Looks up a reference-language entry by its archetype id. */
export function referenceLanguageForArchetype(archetypeId: ArchetypeId): ReferenceLanguageEntry | undefined {
  return referenceLanguages.find((entry) => entry.referenceLanguage === archetypeId);
}

/** The communicationProfile's `true` keys, in schema order — for surfacing
 *  "extended characteristics" without hand-authoring prose. */
export function activeProfileDimensions(entry: ReferenceLanguageEntry): Array<keyof ReferenceLanguageEntry["communicationProfile"]> {
  return (Object.keys(entry.communicationProfile) as Array<keyof ReferenceLanguageEntry["communicationProfile"]>).filter(
    (key) => entry.communicationProfile[key],
  );
}
